import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth, getAuthHeaders } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { BookOpen, Clock, BarChart3, ExternalLink, Inbox, MessageSquare, CreditCard, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('courses');

  const loadData = useCallback(() => {
    const opts = { headers: getAuthHeaders(), withCredentials: true };
    Promise.all([
      axios.get(`${API}/enrollments/my`, opts),
      axios.get(`${API}/messages/my`, opts),
    ]).then(([enr, msg]) => {
      setEnrollments(enr.data);
      setMessages(msg.data);
    }).catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const sendReply = async () => {
    if (!replyContent.trim()) return;
    setSending(true);
    const opts = { headers: getAuthHeaders(), withCredentials: true };
    try {
      await axios.post(`${API}/messages`, {
        user_id: 'admin', content: replyContent, msg_type: 'general'
      }, opts);
      setReplyContent('');
      const { data } = await axios.get(`${API}/messages/my`, opts);
      setMessages(data);
    } catch {}
    setSending(false);
  };

  const statusColor = (s) => {
    if (s === 'confirmed') return 'bg-green-50 text-green-700 border-green-200';
    if (s === 'completed') return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  };

  const paymentColor = (s) => s === 'paid'
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-red-50 text-red-600 border-red-200';

  const unreadCount = messages.filter(m => !m.is_read && m.sender_role === 'admin').length;

  const handleTabClick = async (key) => {
    setActiveTab(key);
    if (key === 'messages' && unreadCount > 0) {
      try {
        const opts = { headers: getAuthHeaders(), withCredentials: true };
        await axios.post(`${API}/messages/mark-read`, {}, opts);
        setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      } catch {}
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFEF9]" data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono tracking-[0.2em] uppercase text-[#0891B2]">Dashboard</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] mt-2 mb-2" style={{ fontFamily: 'Outfit' }}>
            Welcome back, <span className="text-[#0891B2]">{user?.name}</span>
          </h1>
          <p className="text-sm text-gray-500 mb-8">Track your enrolled courses, payments, and messages.</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Enrolled', value: enrollments.length, icon: BookOpen },
            { label: 'Pending', value: enrollments.filter(e => e.status === 'pending').length, icon: Clock },
            { label: 'Confirmed', value: enrollments.filter(e => e.status === 'confirmed').length, icon: BarChart3 },
            { label: 'Messages', value: messages.length, icon: MessageSquare },
          ].map((s) => { const Icon = s.icon; return (
            <div key={s.label} className="neo-card-static p-5 flex items-center gap-4" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#0891B2]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#1a1a2e]" style={{ fontFamily: 'Outfit' }}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            </div>
          ); })}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="dashboard-tabs">
          {[{ key: 'courses', label: 'My Courses', icon: BookOpen },
            { key: 'messages', label: 'Messages', icon: MessageSquare }
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => handleTabClick(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === t.key ? 'bg-cyan-50 text-[#0891B2] border border-cyan-200' : 'text-gray-500 hover:bg-gray-50 border border-transparent'
                }`} data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4" />{t.label}
                {t.key === 'messages' && unreadCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">{unreadCount}</span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#0891B2] border-t-transparent rounded-full animate-spin" /></div>
        ) : activeTab === 'courses' ? (
          /* COURSES TAB */
          enrollments.length === 0 ? (
            <div className="neo-card-static p-12 text-center" data-testid="no-enrollments">
              <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg text-gray-600 mb-2">No enrollments yet</h3>
              <p className="text-sm text-gray-400 mb-6">Browse our programs and enroll in your first one!</p>
              <button onClick={() => navigate('/')} className="btn-cyan text-sm" data-testid="browse-courses-btn">Browse Programs</button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="enrollments-list">
              {enrollments.map((e, i) => (
                <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="neo-card-static p-5" data-testid={`enrollment-card-${e.id}`}>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="text-base font-medium text-[#1a1a2e]">{e.course_title}</h3>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-md border ${statusColor(e.status)}`} data-testid={`status-badge-${e.id}`}>{e.status}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border ${paymentColor(e.payment_status)}`} data-testid={`payment-badge-${e.id}`}>
                          <CreditCard className="w-3 h-3" />{e.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span>{e.course_category}</span>
                        <span>Enrolled: {new Date(e.enrolled_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/course/${e.course_id}`)}
                      className="text-[#0891B2] hover:text-[#0E7490] hover:bg-cyan-50" data-testid={`view-course-${e.id}`}>
                      View Course <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </div>
                  {e.admin_notes && (
                    <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`admin-note-${e.id}`}>
                      <div className="text-xs text-gray-400 mb-1"><i className="fa-solid fa-sticky-note mr-1" />Admin Note:</div>
                      <p className="text-sm text-gray-600 bg-cyan-50/50 rounded-lg px-3 py-2">{e.admin_notes}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )
        ) : (
          /* MESSAGES TAB */
          <div data-testid="messages-section">
            {messages.length === 0 ? (
              <div className="neo-card-static p-12 text-center" data-testid="no-messages">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg text-gray-600 mb-2">No messages yet</h3>
                <p className="text-sm text-gray-400">Your admin will send you class links, timings, and updates here.</p>
              </div>
            ) : (
              <div className="neo-card-static p-5" data-testid="messages-chat">
                <div className="space-y-3 max-h-[400px] overflow-y-auto mb-4 pr-2">
                  {messages.map(m => (
                    <div key={m.id} className={`flex ${m.sender_role === 'student' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                        m.sender_role === 'student'
                          ? 'bg-[#0891B2] text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`} data-testid={`message-${m.id}`}>
                        {m.msg_type !== 'general' && (
                          <div className={`text-xs mb-1 uppercase font-semibold ${m.sender_role === 'student' ? 'text-white/70' : 'text-[#0891B2]'}`}>
                            {m.msg_type.replace('_', ' ')}
                          </div>
                        )}
                        <p>{m.content}</p>
                        <div className={`text-xs mt-1 ${m.sender_role === 'student' ? 'text-white/60' : 'text-gray-400'}`}>
                          {m.sender_name} &middot; {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 flex gap-2">
                  <input type="text" value={replyContent} onChange={e => setReplyContent(e.target.value)}
                    placeholder="Reply to admin..."
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#0891B2]"
                    onKeyDown={e => { if (e.key === 'Enter') sendReply(); }}
                    data-testid="reply-input" />
                  <button onClick={sendReply} disabled={sending}
                    className="btn-cyan !py-2 !px-4 !text-sm flex items-center gap-1" data-testid="send-reply-btn">
                    <Send className="w-4 h-4" />{sending ? '...' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
